import { noop } from "lodash";
import { t } from "@/i18n-js/instance";
import { ErrorBoundary } from "@/react/components/shared/ErrorPages/ErrorBoundary";
import { ChatRoomContextProvider } from "@/react/hooks/chatsV2/useChatRoomContext";
import type { CoursesLesson } from "@/react/types/CoursesLesson";
import { EmbeddableChat } from "@circle-react/components/Chat/EmbeddableChat";
import { ChatThreads } from "@circle-react/components/Chat/MessagingV2/ChatThreads";
import { ChatError } from "@circle-react/components/LiveStreams/ChatError";
import {
  useCurrentSpaceContext,
  usePunditUserContext,
  useSpaceResource,
} from "@circle-react/contexts";
import { isDraftSpace } from "@circle-react/helpers/spaceHelpers";

interface CommentsContentProps {
  lesson: CoursesLesson;
}

// This component merely exists as a wrapper for the
// EmbeddableChat component, can also be used to customize styles
export const CommentsContent = ({ lesson }: CommentsContentProps) => {
  const { currentCommunityMember } = usePunditUserContext();
  const { data: space } = useCurrentSpaceContext();
  const { data: spaceResource } = useSpaceResource({
    spaceId: space?.id,
  });

  if (isDraftSpace(spaceResource) || lesson.status === "draft") {
    return (
      <div className="flex max-w-[352px] flex-1 items-end justify-center">
        <div className="bg-secondary text-dark mx-6 mb-6 rounded-lg p-4 text-center text-sm">
          {t("messaging.course_lesson_chat_disabled")}
        </div>
      </div>
    );
  }

  return (
    <div className="standard-layout-v2 md:w-[360px]">
      {!!currentCommunityMember && (
        <ErrorBoundary renderFunc={ChatError}>
          <ChatRoomContextProvider
            chatRoomUuid={lesson.chat_room_uuid}
            isMessaging={false}
          >
            <EmbeddableChat
              uuid={lesson.chat_room_uuid}
              onNewChatMessage={noop}
              messageBodyClassName="mr-4"
              isInCourseSpace
              displayDayChange
            />
            <ChatThreads />
          </ChatRoomContextProvider>
        </ErrorBoundary>
      )}
    </div>
  );
};
